/**
 * DynamicDimensions - Rock-solid dynamic sizing system
 * 
 * This system ensures:
 * 1. Content never overlaps
 * 2. Callouts expand based on text
 * 3. Footer always anchored correctly
 * 4. Optimal sizes for different use cases
 */

export interface DimensionPreset {
  width: number;
  height: number | 'auto';
  name: string;
  description: string;
}

export interface CalloutConfig {
  title?: string;
  content?: string;
  emphasis?: string;
  backgroundColor?: string;
  borderColor?: string;
  textColor?: string;
  titleColor?: string;
}

export interface ComponentConfig {
  preset?: string;
  customWidth?: number;
  customHeight?: number;
  header?: { title?: string; subtitle?: string; badge?: string };
  content: {
    type: string;
    height: number; // Each content type knows its height
  };
  callouts?: CalloutConfig[];
  footer?: { showCopyright?: boolean };
}

export class DynamicDimensions {
  // Predefined sizes for common use cases
  static readonly PRESETS: Record<string, DimensionPreset> = {
    // Social Media (fixed dimensions required)
    'social-twitter': { 
      width: 1200, 
      height: 675, 
      name: 'Twitter/X Card',
      description: 'Optimal for Twitter timeline (16:9)' 
    },
    'social-facebook': { 
      width: 1200, 
      height: 630, 
      name: 'Facebook Share',
      description: 'Facebook feed optimal (1.91:1)' 
    },
    'social-linkedin': { 
      width: 1200, 
      height: 627, 
      name: 'LinkedIn Post',
      description: 'LinkedIn feed optimal' 
    },
    'social-instagram-square': { 
      width: 1080, 
      height: 1080, 
      name: 'Instagram Square',
      description: 'Instagram feed (1:1)' 
    },
    'social-instagram-story': { 
      width: 1080, 
      height: 1920, 
      name: 'Instagram Story',
      description: 'Instagram/FB story (9:16)' 
    },
    
    // Content Embeds (dynamic height)
    'content-inline': { 
      width: 700, 
      height: 'auto', 
      name: 'Blog Inline',
      description: 'Standard blog width' 
    },
    'content-full': { 
      width: 1200, 
      height: 'auto', 
      name: 'Full Width',
      description: 'Full content width' 
    },
    'content-sidebar': { 
      width: 350, 
      height: 'auto', 
      name: 'Sidebar',
      description: 'Sidebar widget size' 
    },
    
    // Presentations
    'presentation-16:9': { 
      width: 1920, 
      height: 1080, 
      name: 'HD Presentation',
      description: 'PowerPoint/Keynote HD' 
    },
    'presentation-4:3': { 
      width: 1024, 
      height: 768, 
      name: 'Standard Presentation',
      description: 'Traditional 4:3 slides' 
    },
    
    // Default
    'default': { 
      width: 700, 
      height: 480, 
      name: 'Default',
      description: 'Standard component size' 
    }
  };

  // Component sizing constants
  private static readonly SPACING = {
    HEADER_HEIGHT: 60,
    HEADER_GAP: 20,
    CONTENT_GAP: 20,
    CALLOUT_GAP: 20,
    FOOTER_HEIGHT: 30,
    CALLOUT_PADDING_TOP: 20,
    CALLOUT_PADDING_BOTTOM: 15,
    CALLOUT_LINE_HEIGHT: 15,
    CALLOUT_LINE_GAP: 5
  };

  /**
   * Get dimensions for a component
   */
  static getDimensions(config: ComponentConfig): { width: number; height: number } {
    // Priority: custom > preset > default
    let width = config.customWidth || 700;
    let height = config.customHeight || 480;

    if (config.preset && this.PRESETS[config.preset]) {
      const preset = this.PRESETS[config.preset];
      width = preset.width;
      height = preset.height === 'auto' 
        ? this.calculateAutoHeight(config, width)
        : preset.height;
    } else if (!config.customHeight) {
      // No preset and no custom height = calculate it
      height = this.calculateAutoHeight(config, width);
    }

    return { width, height };
  }

  /**
   * Calculate automatic height based on content
   */
  static calculateAutoHeight(config: ComponentConfig, width: number): number {
    let height = 0;
    
    // Header section
    if (config.header) {
      height += this.SPACING.HEADER_HEIGHT;
      height += this.SPACING.HEADER_GAP;
    }
    
    // Main content (each component type provides its height)
    height += config.content.height;
    height += this.SPACING.CONTENT_GAP;
    
    // Callout boxes - DYNAMIC HEIGHT
    if (config.callouts && config.callouts.length > 0) {
      config.callouts.forEach((callout, index) => {
        height += this.calculateCalloutHeight(callout, width);
        if (index < config.callouts!.length - 1) {
          height += this.SPACING.CALLOUT_GAP;
        }
      });
      height += this.SPACING.CONTENT_GAP;
    }
    
    // Footer
    height += this.SPACING.FOOTER_HEIGHT;
    
    return Math.ceil(height); // Round up to avoid fractional pixels
  }

  /**
   * Calculate callout box height based on text content and container width
   */
  static calculateCalloutHeight(callout: CalloutConfig, containerWidth: number): number {
    // Responsive padding based on container width
    const padding = Math.max(20, Math.min(60, containerWidth * 0.08));
    const USABLE_WIDTH = containerWidth - (padding * 2);
    
    // Responsive font sizes
    const titleFontSize = Math.max(10, Math.min(13, containerWidth * 0.018));
    const contentFontSize = Math.max(9, Math.min(11, containerWidth * 0.016));
    const emphasisFontSize = Math.max(10, Math.min(12, containerWidth * 0.017));
    
    // Responsive line height
    const lineHeight = Math.max(12, Math.min(15, containerWidth * 0.021));
    
    // Reduced padding for tighter fit
    let height = 10; // Reduced from 20px to 10px
    
    // Calculate lines for each text element with responsive sizing
    if (callout.title) {
      const lines = this.estimateTextLines(callout.title, USABLE_WIDTH, titleFontSize);
      height += lines * lineHeight;
      if (callout.content || callout.emphasis) {
        height += 2; // Reduced from 5px to 2px
      }
    }
    
    if (callout.content) {
      const lines = this.estimateTextLines(callout.content, USABLE_WIDTH, contentFontSize);
      height += lines * lineHeight;
      if (callout.emphasis) {
        height += 2; // Reduced from 5px to 2px
      }
    }
    
    if (callout.emphasis) {
      const lines = this.estimateTextLines(callout.emphasis, USABLE_WIDTH, emphasisFontSize);
      height += lines * lineHeight;
    }
    
    height += 8; // Reduced from 15px to 8px
    
    // Responsive minimum height - reduced to fit content better
    const minHeight = Math.max(40, Math.min(55, containerWidth * 0.12));
    
    return Math.max(height, minHeight);
  }

  /**
   * Estimate number of lines text will take
   * Using Space Grotesk font metrics
   */
  private static estimateTextLines(text: string, maxWidth: number, fontSize: number): number {
    // Space Grotesk character width ratios (approximate)
    const charWidthRatio = fontSize * 0.55; // Slightly narrower than 0.6
    const avgCharWidth = charWidthRatio;
    const charsPerLine = Math.floor(maxWidth / avgCharWidth);
    
    // Account for word wrapping (add 10% buffer)
    const effectiveCharsPerLine = Math.floor(charsPerLine * 0.9);
    
    return Math.ceil(text.length / effectiveCharsPerLine);
  }

  /**
   * Get all element positions for a given configuration
   * This ensures PERFECT placement every time
   */
  static getElementPositions(config: ComponentConfig): Record<string, any> {
    const { width, height } = this.getDimensions(config);
    const positions: Record<string, any> = {};
    let currentY = 0;

    // Header position
    if (config.header) {
      positions.header = {
        y: currentY,
        height: this.SPACING.HEADER_HEIGHT
      };
      currentY += this.SPACING.HEADER_HEIGHT + this.SPACING.HEADER_GAP;
    }

    // Content position
    positions.content = {
      y: currentY,
      height: config.content.height
    };
    currentY += config.content.height + this.SPACING.CONTENT_GAP;

    // Callout positions with responsive padding
    if (config.callouts && config.callouts.length > 0) {
      positions.callouts = [];
      const padding = Math.max(20, Math.min(60, width * 0.08));
      
      config.callouts.forEach((callout, index) => {
        const calloutHeight = this.calculateCalloutHeight(callout, width);
        positions.callouts.push({
          y: currentY,
          height: calloutHeight,
          x: padding,
          width: width - (padding * 2),
          padding: padding // Store for template use
        });
        currentY += calloutHeight;
        if (index < config.callouts!.length - 1) {
          currentY += this.SPACING.CALLOUT_GAP;
        }
      });
    }

    // Footer position (ALWAYS calculated from bottom)
    positions.footer = {
      y: height - this.SPACING.FOOTER_HEIGHT,
      height: 27,
      copyrightY: height - 12,
      faviconY: height - 22,
      faviconX: width - 40
    };

    return positions;
  }

  /**
   * Validate that all elements fit without overlap
   */
  static validate(config: ComponentConfig): { valid: boolean; errors: string[] } {
    const errors: string[] = [];
    const positions = this.getElementPositions(config);
    const { height } = this.getDimensions(config);

    // Check if content fits
    let maxY = 0;
    if (positions.header) {
      maxY = positions.header.y + positions.header.height;
    }
    if (positions.content) {
      maxY = Math.max(maxY, positions.content.y + positions.content.height);
    }
    if (positions.callouts) {
      const lastCallout = positions.callouts[positions.callouts.length - 1];
      maxY = Math.max(maxY, lastCallout.y + lastCallout.height);
    }

    // Ensure footer doesn't overlap content
    const footerStart = positions.footer.y;
    const minGap = 20;
    
    if (maxY + minGap > footerStart) {
      errors.push(`Content overlaps footer. Need ${maxY + minGap - footerStart}px more height`);
    }

    // Check minimum dimensions
    const { width } = this.getDimensions(config);
    if (width < 350) {
      errors.push('Width must be at least 350px for readable content');
    }
    if (height < 200) {
      errors.push('Height must be at least 200px');
    }

    return {
      valid: errors.length === 0,
      errors
    };
  }
}

// Export for use in components
export default DynamicDimensions;